import axios from 'axios';
import sysconfigApi from '@/api/sysconfig';
import MD5 from 'md5.js';
import { Buffer } from 'safe-buffer';

const CHUNK_SIZE = 20 * 1024 * 1024;

function getPureUrl(url = '') {
    return url.split('?')[0];
}

function getFileSuffix(name = '') {
    const index = name.lastIndexOf('.');
    if (index === -1) return '';
    return name.substring(index + 1).toLowerCase();
}

function randomString(len = 12) {
    const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
    let randomStr = '';
    for (let i = 0; i < len; i++) {
        randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return randomStr + Date.now();
}

/** 从业务接口返回值中取出真实 URL / uploadId */
function unwrapApiData(res) {
    if (res == null) return '';
    if (typeof res === 'string') return res;
    if (res.data !== undefined && res.data !== null) {
        const inner = res.data;
        if (typeof inner === 'string') return inner;
        if (inner && typeof inner.url === 'string') return inner.url;
        if (inner && inner.data !== undefined) return inner.data;
    }
    return '';
}

function getVideoDuration(file) {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        const objectUrl = URL.createObjectURL(file);
        video.preload = 'metadata';
        video.src = objectUrl;
        video.onloadedmetadata = () => {
            const durationMs = Math.floor((video.duration || 0) * 1000);
            URL.revokeObjectURL(objectUrl);
            resolve(durationMs);
        };
        video.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(0);
        };
    });
}

function getVideoFrameBlob(file) {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        const objectUrl = URL.createObjectURL(file);
        video.preload = 'auto';
        video.muted = true;
        video.src = objectUrl;

        video.onloadeddata = () => {
            const width = 200;
            const height = 100;
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(objectUrl);
                return resolve(null);
            }

            const videoWidth = video.videoWidth || width;
            const videoHeight = video.videoHeight || height;
            const ratio = Math.min(width / videoWidth, height / videoHeight);
            const drawWidth = videoWidth * ratio;
            const drawHeight = videoHeight * ratio;
            const dx = (width - drawWidth) / 2;
            const dy = (height - drawHeight) / 2;

            // 清空后按 contain 方式居中绘制，避免视频封面被拉伸
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(video, dx, dy, drawWidth, drawHeight);
            canvas.toBlob((blob) => {
                URL.revokeObjectURL(objectUrl);
                resolve(blob || null);
            }, 'image/png');
        };

        video.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
        };
    });
}

async function putBySignedUrl(url, fileOrBlob, onUploadProgress) {
    if (!url) {
        throw new Error('临时上传 URL 为空');
    }
    return axios.put(url, fileOrBlob, {
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress,
    });
}

async function computeMd5(file, chunkSize = CHUNK_SIZE) {
    const hasher = new MD5();
    let offset = 0;
    while (offset < file.size) {
        const blobPart = file.slice(offset, offset + chunkSize);
        // eslint-disable-next-line no-await-in-loop
        const arrayBuffer = await blobPart.arrayBuffer();
        hasher.update(Buffer.from(arrayBuffer));
        offset += chunkSize;
    }
    return hasher.digest('hex');
}

async function singleUpload(file, onProgress) {
    const signRet = await sysconfigApi.getTemporarySignature({
        objectName: file.name,
    });
    const signedUrl = unwrapApiData(signRet);
    await putBySignedUrl(signedUrl, file, onProgress);
    return getPureUrl(signedUrl);
}

async function multipartUpload(file, onProgress) {
    const uploadIdRet = await sysconfigApi.getMultiPartUploadId({
        objectName: file.name,
    });
    const uploadId = unwrapApiData(uploadIdRet);
    if (!uploadId) {
        throw new Error('getMultiPartUploadId 未返回 uploadId');
    }

    const totalPart = Math.ceil(file.size / CHUNK_SIZE);
    const partEtags = [];
    const loadedMap = {};

    for (let partNumber = 1; partNumber <= totalPart; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blobPart = file.slice(start, end);
        const signRet = await sysconfigApi.getSectionTemporarySignature({
            uploadId,
            partNumber,
        });
        const signedUrl = unwrapApiData(signRet);
        const response = await putBySignedUrl(signedUrl, blobPart, (event) => {
            if (!event || !event.total) return;
            loadedMap[partNumber] = event.loaded;
            const loaded = Object.keys(loadedMap).reduce((sum, key) => sum + loadedMap[key], 0);
            if (onProgress) {
                onProgress({ loaded, total: file.size });
            }
        });

        partEtags.push({
            etag: response.headers.etag || response.headers.ETag || '',
            partNumber,
        });
    }

    const completeRet = await sysconfigApi.getCompleteUrl({ uploadId });
    const completeUrl = unwrapApiData(completeRet);
    let content = '<CompleteMultipartUpload>';
    partEtags.forEach((item) => {
        content += '<Part>';
        content += `<PartNumber>${item.partNumber}</PartNumber>`;
        content += `<ETag>${item.etag}</ETag>`;
        content += '</Part>';
    });
    content += '</CompleteMultipartUpload>';

    await axios.post(completeUrl, content, {
        headers: { 'Content-Type': 'application/octet-stream' },
    });
    return getPureUrl(completeUrl);
}

async function uploadBySignature(file, options = {}) {
    const { onProgress } = options;

    const useMultipart = file.size > CHUNK_SIZE;
    const url = useMultipart
        ? await multipartUpload(file, onProgress)
        : await singleUpload(file, onProgress);

    if (!url) {
        throw new Error('上传完成但文件 URL 为空');
    }

    let md5 = '';
    try {
        md5 = await computeMd5(file);
    } catch (err) {
        // MD5 失败不阻断上传
    }

    const suffix = getFileSuffix(file.name);
    let duration = 0;
    let fileImageUrl = '';
    if (suffix === 'mp4') {
        duration = await getVideoDuration(file);
        const frameBlob = await getVideoFrameBlob(file);
        if (frameBlob) {
            const frameName = `${randomString(8)}.png`;
            const frameFile = new File([frameBlob], frameName, { type: 'image/png' });
            try {
                fileImageUrl = await singleUpload(frameFile);
            } catch (err) {
                // 封面失败不阻断
            }
        }
    }

    return {
        name: file.name,
        fileName: file.name,
        size: file.size,
        url,
        file: url,
        md5,
        fileSuffix: suffix,
        duration,
        fileImageUrl: fileImageUrl || url,
    };
}

export default uploadBySignature;
